import * as React from 'react';
import { LuWandSparkles } from 'react-icons/lu';
import { t } from '../../services/HelperService';
import { LATEST_API_VERSION, normalizeApiVersion } from '../../services/ApiVersionService';
import { useWorkflowEditor } from '../workflow/WorkflowEditorContext';

/**
 * The AI assistant entry point: a purple wand icon button that pops once shortly
 * after mount to draw attention (see .ai-wand-button in custom.css).
 * Set overlay when rendered on top of an input so it gets a legible backdrop.
 *
 * The AI authors only in the latest API version. Inside a workflow node / custom setting /
 * card scope (WorkflowNodeScopeProvider) that still reads legacy metadata the wand is
 * disabled with a hint, so a v3-authored query is never dropped into a v2 item; the
 * "Upgrade to API v3" action beside the version badge is the way forward.
 */
export const AiWandButton = ({ onClick, title, size = 16, tagName, overlay, style, disabled }) => {
  const editor = useWorkflowEditor();
  const scopeVersion = editor?.node?.metadataVersion;
  const legacyScope = !!scopeVersion && normalizeApiVersion(scopeVersion, LATEST_API_VERSION) !== LATEST_API_VERSION;
  const isDisabled = !!disabled || legacyScope;
  const effectiveTitle = legacyScope
    ? `${t('The AI assistant authors in API')} ${LATEST_API_VERSION}. ${t('Upgrade this item first (see the version badge).')}`
    : title;
  return (
    <button
      type="button"
      className={overlay ? 'ai-wand-button ai-wand-overlay' : 'ai-wand-button'}
      data-tagname={tagName}
      title={effectiveTitle}
      aria-label={effectiveTitle}
      aria-disabled={isDisabled ? 'true' : undefined}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      style={isDisabled ? { ...(style ?? {}), opacity: 0.45, cursor: 'not-allowed' } : style}
    >
      <LuWandSparkles size={size} />
    </button>
  );
};
